import React, { useRef, useState } from 'react';
import ReportUserModal from './ReportUserModal';
import BlockUserModal from './BlockUserModal';
import UnblockUserModal from './UnblockUserModal';

/**
 * Orquesta reportar, bloquear y desbloquear a `user` con una sola ventana a
 * la vez, como la app móvil: para pasar de una a otra (reporte → bloquear,
 * bloqueo → reportar) se cierra la actual y la siguiente se abre cuando la
 * anterior terminó de cerrarse (afterLeave).
 *
 * Callbacks: onBlocked(result), onUnblocked(), onGoToChat(chatId) para
 * reclamar, y onBlockFinished() al cerrar el diálogo después de bloquear
 * (en un perfil, para salir de él).
 */
export default function useUserSafety({ user, canBlock = true, blockedByMe = false, onBlocked, onUnblocked, onGoToChat, onBlockFinished }) {
  const [active, setActive] = useState(null); // report | block | unblock
  const [reportOffersBlock, setReportOffersBlock] = useState(true);
  const nextRef = useRef(null);
  const blockedNowRef = useRef(false);

  const chain = (next) => { nextRef.current = next; setActive(null); };
  const afterLeave = () => {
    const next = nextRef.current;
    nextRef.current = null;
    if (next) setActive(next);
  };

  const openReport = () => { blockedNowRef.current = false; setReportOffersBlock(canBlock && !blockedByMe); setActive('report'); };
  const openBlock = () => { blockedNowRef.current = false; setActive('block'); };
  const openUnblock = () => setActive('unblock');

  // Cerrar después de bloquear (desde el bloqueo o desde el reporte que se
  // ofreció después) termina el flujo de bloqueo.
  const closeAfterBlock = () => {
    setActive(null);
    if (blockedNowRef.current) {
      blockedNowRef.current = false;
      onBlockFinished?.();
    }
  };

  const modals = (
    <>
      <ReportUserModal
        open={active === 'report'}
        user={user}
        onClose={closeAfterBlock}
        afterLeave={afterLeave}
        onRequestBlock={reportOffersBlock ? () => chain('block') : null}
      />
      <BlockUserModal
        open={active === 'block'}
        user={user}
        onClose={closeAfterBlock}
        afterLeave={afterLeave}
        onBlocked={(result) => { blockedNowRef.current = true; onBlocked?.(result); }}
        onRequestReport={() => { setReportOffersBlock(false); chain('report'); }}
        onGoToChat={(chatId) => { setActive(null); onGoToChat?.(chatId); }}
      />
      <UnblockUserModal
        open={active === 'unblock'}
        user={user}
        onClose={() => setActive(null)}
        afterLeave={afterLeave}
        onUnblocked={onUnblocked}
      />
    </>
  );

  return { openReport, openBlock, openUnblock, modals };
}
